import { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Phone, Loader2 } from "lucide-react";

/**
 * Email/Phone OTP verification dialog.
 * Props:
 *  - open, onOpenChange
 *  - channel: "email" | "phone"
 *  - onVerified()
 */
export default function OtpDialog({ open, onOpenChange, channel = "email", onVerified }) {
  const [step, setStep] = useState("send"); // send | confirm
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [demoCode, setDemoCode] = useState(null);

  const isEmail = channel === "email";
  const label = isEmail ? "e-mail" : "téléphone";
  const Icon = isEmail ? Mail : Phone;

  const sendCode = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/verify/otp/request", { channel });
      setStep("confirm");
      if (data.demo_code) {
        setDemoCode(data.demo_code);
        toast.info(`Code démo : ${data.demo_code}`);
      } else {
        toast.success(`Code envoyé par ${label}`);
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const confirmCode = async () => {
    if (code.length !== 6) { toast.error("Le code fait 6 chiffres"); return; }
    setBusy(true);
    try {
      await api.post("/verify/otp/confirm", { channel, code });
      toast.success(`${isEmail ? "Email" : "Téléphone"} vérifié !`);
      onVerified?.();
      onOpenChange(false);
      setStep("send"); setCode(""); setDemoCode(null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm rounded-2xl" data-testid={`otp-dialog-${channel}`}>
        <DialogHeader>
          <div className="mx-auto h-12 w-12 rounded-2xl bg-brand-light text-brand flex items-center justify-center mb-2">
            <Icon className="w-6 h-6" />
          </div>
          <DialogTitle className="font-heading text-center">Vérifier votre {label}</DialogTitle>
          <DialogDescription className="text-center">
            {step === "send"
              ? `Nous allons vous envoyer un code à 6 chiffres pour valider votre ${label}.`
              : "Entrez le code à 6 chiffres reçu."}
          </DialogDescription>
        </DialogHeader>
        {step === "send" ? (
          <div className="pt-2">
            <Button onClick={sendCode} disabled={busy} className="w-full rounded-full bg-brand hover:bg-brand-hover" data-testid={`otp-send-${channel}`}>
              {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Recevoir le code
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex justify-center">
              <InputOTP maxLength={6} value={code} onChange={setCode} data-testid={`otp-input-${channel}`}>
                <InputOTPGroup>
                  {[0,1,2,3,4,5].map((i) => <InputOTPSlot key={i} index={i} />)}
                </InputOTPGroup>
              </InputOTP>
            </div>
            {demoCode && (
              <div className="text-center text-xs text-slate2">
                Mode démo : <b className="text-brand">{demoCode}</b>
              </div>
            )}
            <Button onClick={confirmCode} disabled={busy || code.length !== 6} className="w-full rounded-full bg-brand hover:bg-brand-hover" data-testid={`otp-confirm-${channel}`}>
              {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Valider
            </Button>
            <button onClick={() => setStep("send")} className="w-full text-xs text-slate2 tap-scale" data-testid={`otp-resend-${channel}`}>
              Renvoyer un code
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
